import { NameColumn } from '@/components/NameColumn';
import type { Columns } from '@/components/table/types';
import type { ADObject } from '@/types';

/**
 * Builds column definitions for Active Directory Object entity tables
 *
 * Returns columns for:
 * - label: Object type (User/Group/Computer/etc)
 * - identifier: Display name with fallback to name or identifier
 * - objectid: Active Directory object ID
 *
 * The identifier column uses NameColumn component to display:
 * - Primary identifier (displayname > name > identifier) in bold purple text
 * - Object ID below in smaller gray text
 *
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildADObjectColumns();
 * // Returns array with label, identifier, and objectid columns
 * // Each column has defensive null checks returning '-' for missing values
 */
export function buildADObjectColumns(): Columns<ADObject> {
  return [
    {
      id: 'label',
      label: 'Object Type',
      cell: (item: ADObject) => item?.label || '-',
    },
    {
      id: 'identifier',
      label: 'Identifier',
      cell: (item: ADObject) => {
        // Prioritize displayname, fallback to name, then identifier
        const primaryName = item?.displayname || item?.name || item?.identifier;

        if (!primaryName) {
          return '-';
        }

        return <NameColumn name={primaryName} description={item.objectid} />;
      },
    },
    {
      id: 'objectid',
      label: 'Object ID',
      cell: (item: ADObject) => item?.objectid || '-',
    },
  ];
}
